import { euro, euroDezimal } from "./preisAnalyse.js";

/**
 * Die Fragen, die vor jeder Anfrage kommen — je Angebot aus seinen Daten
 * beantwortet.
 *
 * Jede Antwort steht nur da, wenn die Angabe wirklich vorliegt. Lieber vier
 * Fragen mit belegten Antworten als acht, von denen die Hälfte "das hängt
 * vom Anbieter ab" sagt.
 */
export function angebotFaq(angebot) {
  if (!angebot) return [];

  const { partnerName, booth, packages, delivery, cancellation, printLayouts } =
    angebot;
  const grundpreis = booth?.price || 0;
  const fragen = [];

  if (packages?.length) {
    const preise = packages.map((p) => grundpreis + (p.price || 0));
    const guenstigstes = packages[preise.indexOf(Math.min(...preise))];
    fragen.push({
      question: `Was kostet die Fotobox von ${partnerName}?`,
      answer:
        packages.length > 1
          ? `Das günstigste Paket ist ${guenstigstes.name} für ${euro(
              Math.min(...preise)
            )} €, das umfangreichste ${euro(Math.max(...preise))} €. Insgesamt stehen ${
              packages.length
            } Pakete zur Wahl; der Unterschied liegt vor allem in der Zahl der Ausdrucke.`
          : `${guenstigstes.name} kostet ${euro(Math.min(...preise))} €. Ein weiteres Paket bietet dieser Anbieter nicht an.`,
    });
  }

  const mitDruck = (packages || []).filter(
    (p) => p.printer?.paperStorageSize || p.printFlatrate
  );
  if (mitDruck.length) {
    const flat = mitDruck.find((p) => p.printFlatrate);
    const groesstes = Math.max(
      ...mitDruck.map((p) => p.printer?.paperStorageSize || 0)
    );
    fragen.push({
      question: "Wie viele Bilder druckt die Box?",
      answer: flat
        ? `Das Paket ${flat.name} druckt ohne Begrenzung — ihr müsst am Abend nicht rechnen. ${
            mitDruck.length > 1
              ? `In den übrigen Druckpaketen ist Papier für bis zu ${groesstes} Ausdrucke enthalten.`
              : ""
          }`.trim()
        : `Im größten Druckpaket ist Papier für ${groesstes} Ausdrucke enthalten. Bei zwei bis drei Bildern pro Gast reicht das für rund ${Math.floor(
            groesstes / 2.5
          )} Gäste — mit Fotobox-Gästebuch für die Hälfte, weil dann jedes Motiv zweimal gedruckt wird. Die Zahl ist die mitgelieferte Papiermenge, nicht eine Grenze des Druckers; für den Abend ist sie aber die praktische Grenze.`,
    });
  }

  const versand = delivery?.post;
  const lieferung = delivery?.selfDelivery;

  if (versand) {
    fragen.push({
      question: "Wann kommt die Box an?",
      answer: `${
        versand.daysBeforeStart === versand.daysBeforeEnd
          ? `${versand.daysBeforeStart} ${versand.daysBeforeStart === 1 ? "Tag" : "Tage"}`
          : `${versand.daysBeforeStart} bis ${versand.daysBeforeEnd} Tage`
      } vor eurem Termin, per Paket an die angegebene Adresse. ${
        versand.costs === 0
          ? "Hin- und Rückversand sind im Preis enthalten."
          : `Der Versand kostet ${euro(versand.costs)} € zusätzlich.`
      } Aufgebaut ist sie in einer Viertelstunde.`,
    });
  } else if (lieferung?.city) {
    const staffel = (lieferung.ranges || []).filter((r) => r && r.end);
    const frei = staffel.find((r) => !r.pricePerKm);
    fragen.push({
      question: "Liefert der Anbieter zu meiner Location?",
      answer: `Geliefert wird ab ${lieferung.city}${
        lieferung.zip ? ` (${lieferung.zip})` : ""
      }.${
        frei ? ` Bis ${frei.end} km kostet die Anfahrt nichts extra.` : ""
      }${
        staffel.length > 1
          ? ` Danach kommen ${euroDezimal(
              staffel[staffel.length - 1].pricePerKm
            )} € je Kilometer dazu, bis maximal ${Math.max(
              ...staffel.map((r) => r.end)
            )} km.`
          : ""
      } Gib deine Postleitzahl ein, dann rechnen wir es aus.`,
    });
  }

  if (booth?.camType) {
    fragen.push({
      question: "Werden die Bilder auch abends gut?",
      answer: `Verbaut ist eine ${booth.camType}${
        booth.lightType ? ` mit ${booth.lightType}` : ""
      }. ${
        /Spiegelreflex|DSLM|System/i.test(booth.camType)
          ? "Damit werden auch bei wenig Licht scharfe Bilder."
          : "Bei guter Beleuchtung reicht das; in sehr dunklen Räumen sieht man den Unterschied zu einer Spiegelreflexkamera."
      }${
        /Blitz/i.test(booth.lightType || "")
          ? " Der Blitz friert Bewegung ein — wichtig, sobald vor der Box getanzt wird."
          : ""
      }`,
    });
  }

  if (booth?.digital?.gallery || booth?.digital?.usb || booth?.digital?.phone) {
    fragen.push({
      question: "Bekomme ich die Bilder auch digital?",
      answer: `Ja: ${[
        booth.digital.gallery && "über eine Online-Galerie",
        booth.digital.phone && "direkt aufs Handy",
        booth.digital.usb && "auf USB-Stick",
      ]
        .filter(Boolean)
        .join(", ")}. Der Ausdruck bleibt beim Gast — so habt ihr die Bilder trotzdem.`,
    });
  }

  if (printLayouts?.ownLayoutPossible) {
    fragen.push({
      question: "Kann ich ein eigenes Layout verwenden?",
      answer: `Ja. Ihr könnt eine eigene Vorlage liefern — mit Namen und Datum, einem Motto oder dem Firmenlogo. Schickt sie einige Tage vor dem Termin, damit sie eingerichtet werden kann.${
        printLayouts.layoutCount
          ? ` Alternativ stehen ${printLayouts.layoutCount} fertige Vorlagen zur Auswahl.`
          : ""
      }`,
    });
  }

  if (cancellation?.freeReschedule) {
    fragen.push({
      question: "Was passiert, wenn sich der Termin verschiebt?",
      answer: `Eine Umbuchung ist bis ${
        cancellation.freeRescheduleDays || 0
      } ${
        cancellation.freeRescheduleDays === 1 ? "Tag" : "Tage"
      } vor der Veranstaltung kostenlos.`,
    });
  } else if (cancellation?.refundRanges?.length) {
    const sortiert = [...cancellation.refundRanges].sort(
      (a, b) => b.startDays - a.startDays
    );
    const beste = sortiert[0];
    fragen.push({
      question: "Kann ich stornieren?",
      answer: `Ja. Bei einer Stornierung mehr als ${beste.startDays} Tage vor dem Termin bekommt ihr ${beste.refundPercent} % zurück; danach sinkt die Erstattung. Die vollständige Staffel steht oben auf dieser Seite.`,
    });
  }

  return fragen;
}
